/** Stan modułów aplikacji — z niego menu, trasy i pulpit wiedzą, co pokazać. */

import { useQuery } from '@tanstack/react-query'

import { modulesApi } from '@/api/endpoints'
import type { AppModule, ModuleSlug } from '@/api/types'

export const MODULES_QUERY_KEY = ['modules'] as const

/**
 * Lista modułów. Zmienia się wyjątkowo rzadko, więc trzymamy ją długo
 * w pamięci podręcznej zamiast odpytywać przy każdym przejściu między
 * ekranami.
 */
export function useModules() {
  return useQuery({
    queryKey: MODULES_QUERY_KEY,
    queryFn: () => modulesApi.list(),
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Zbiór włączonych modułów albo `null`, dopóki odpowiedź nie dotarła.
 *
 * `null` znaczy „jeszcze nie wiadomo”, a nie „nic nie działa” — interfejs
 * traktuje ten stan jak komplet modułów, tak samo jak backend traktuje brak
 * wiersza. Odwrotnie menu migałoby przy każdym wejściu do aplikacji.
 */
export function useEnabledModules(): ReadonlySet<ModuleSlug> | null {
  const { data } = useModules()
  if (!data) return null
  return new Set(data.filter((item) => item.is_enabled).map((item) => item.slug))
}

/** Czy moduł działa. Do czasu odpowiedzi zakładamy, że tak. */
export function useModuleEnabled(slug: ModuleSlug): boolean {
  const enabled = useEnabledModules()
  return enabled === null || enabled.has(slug)
}

export function sortModules(modules: AppModule[]): AppModule[] {
  return [...modules].sort((a, b) => a.sort_order - b.sort_order)
}
